/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {LoadingButton} from "@mui/lab";
import {
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography
} from "@mui/material";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {StringParam, useQueryParam} from "use-query-params";
import Loader from "../../../../../../../components/Loader/Loader";
import {useMsal} from "@azure/msal-react";
import InfoIcon from '@mui/icons-material/Info';
import queryString from "query-string";
import {toast} from 'react-hot-toast';
import {ARGOS_GREEN, ARGOS_PENDING_COLOR, ARGOS_PROGRESS_COLOR} from "../../../../../../../../constants/colors"
import {usePostRequest} from "../../../../../../../../api/helper";
import {requestIdToken} from "../../../../../../../../auth/authConfig";
import VAR_COMMON_CONSTANTS from "../../../../../../../../constants/globalVariables";
import {theme} from "../../../../../../../../constants/theme";
import {
  hasThisAccessLevelToRouteKey,
  useGetIsUserAdmin,
  useHasWriteAccess
} from "../../../../../../../../redux/ducks/AccessControl/accessControlHelpers";
import {selectCurrentUser} from "../../../../../../../../redux/ducks/AccessControl/slice";
import {EUserPermissionAccess} from "../../../../../../../../redux/ducks/AccessControl/types";
import {
  selectBenchmarkData,
  selectCoverageRequirementsClipboard,
  selectCoverageRequirementsDataState,
  selectDatasetsExecutionStatus,
  selectDatasetsLocalState,
  selectIsCancellingDatasets,
  selectMergeSetsLocalState,
  setBenchmarkData,
  setCoverageRequirementsClipboard,
} from "../../../../../../../../redux/ducks/CoverageRequirements/slice";
import {selectGlobalDialogState} from "../../../../../../../../redux/ducks/GlobalDialog/slice";
import {useAppDispatch, useAppSelector} from "../../../../../../../../redux/hooks";
import {EArgosRouteKeys} from "../../../../../../../../Routes/constants";
import ArgosButton from "../../../../../../../components/Buttons/ArgosButton";
import ArgosFlexbox from "../../../../../../../components/Containers/ArgosFlexbox";
import BoxWithCenteredContent from "../../../../../../../components/Containers/BoxWithCenteredContent";
import ArgosDialog, {StyledArgosDialog} from "../../../../../../../components/Dialogs/ArgosDialog";
import RedlineAppDialog from '../../../../../../../components/Dialogs/RedlineAppDialogs/RedlineAppDialog';
import ArgosCheckbox from "../../../../../../../components/Form/Checkbox/ArgosCheckbox";
import ArgosMenuItem from "../../../../../../../components/Form/Select/ArgosMenuItem";
import {
  ArgosBulkEditIcon,
  ArgosMergeIcon,
  ArgosReloadIcon
} from "../../../../../../../components/Icons/ArgosActionIcons";
import {
  ArgosCancelIcon,
  ArgosCopy,
  ArgosCopyAll,
  ArgosDeleteAllIcon,
  ArgosDeleteIcon,
  ArgosExecuteIcon
} from "../../../../../../../components/Icons/ArgosDatasetControlPanelIcons";
import {ArgosDatasetIcon, ArgosFolderIcon, ArgosStudyIcon} from "../../../../../../../components/Icons/ArgosTreeIcons";
import {NestedBulletList} from "../../../../../../../components/Lists/BulletList";
import CheckboxDraggableTree from "../../../../../../../components/Tree/CheckboxDraggableTree";
import {
  getTreeItemColor
} from "./CustomTreeItem";
import {
  checkRegenMergeEndpoint,
  COVERAGE_REQUIREMENTS_CONSTANTS,
  coverageRequirementsDataEndpoint,
  coverageRequirementsDetails,
  coverageRequirementsStudiesDataEndpoint,
  DatasetType,
  generateDatasetsEndpoint,
  mergeDatasetsUpdateEndpoint,
  usersDataEndpoint
} from "../../../../../constants";
import {
  contextMenuMergedDataset,
  contextMenuMergedFolder,
  contextMenuMergeSet,
  contextMenuRawDataset,
  contextMenuRawFolder,
  contextMenuRawStudy,
  COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM,
  coverageRequirementsCopyDataEndpoint,
  coverageRequirementsCopyDataToStudyEndpoint,
  coverageRequirementsModifyTreeEndpoint,
  EDatasetExecutionStatus,
  IN_PROGRESS_STATUSES,
  MERGE_MODES,
  MERGED_DATASET_TREE_TYPE,
  REDLINE_DIALOG_OPTIONS,
  SELECTED_DATASET_ID_URL_PARAM,
  SINGLE_DATASET_TREE_TYPE,
  SINGLE_DATASET_TYPE_STR,
  TREE_OPERATIONS,
  TREE_OPERATIONS_SWITCH_OPTIONS
} from "../../../../constants";
import {dialogHeight, dialogWidth, User} from "../../Dialogs/constants";
import MergeDatasetsDialog from "../../Dialogs/MergeDatasetsDialog";
import {findNodePath} from "../../helper/helper";
import {Parent} from "./CoverageRequirementsTreeComponent2";
import CustomTreeItem from "./CustomTreeItem";
import TreeItemContextMenu from "./TreeItemContextMenu";
import ADAS_PROGRAMS_CONSTANTS from "../../../../../../AdasPrograms/constants";
import BulkEditDialog from "../../Dialogs/BulkEdit/BulkEditDialog";
import UpdateOwnerDialog from "../../Dialogs/UpdateOwnerDialog";
import {BiUser} from "react-icons/bi";
import {handleExecutionStatusUpdate} from "../LeftPanelCoverageRequirements";

export interface ITreeComponentProps {
  treeObjectData: any
  treeType: string
  studyId: string
  studyName: string
  isPlanOfRecord?: boolean
  setDatasetPath: any
  setDatasetChange: any
  datasetChange: any
  onClickAbort?: any
  setSeed?: any
  fetchStudy: any
  isReadOnlyStudy?: boolean
}

export interface ITreeNode {
  nodeId: string,
  name: string,
  owner?: string,
  description?: string,
  children?: any[],
  nodeType: string,
  datasetId?: string,
  executionStatus?: EDatasetExecutionStatus,
  meta?: any,
  objectId?: string
}

export interface ITreeNodePopUp {
  tree: any;
  studyId: any;
  id: any,
  map: any,
  nodeId: string,
  name: string,
  owner?: string,
  description?: string,
  children?: any[],
  nodeType: string,
  datasetId?: string,
  executionStatus?: EDatasetExecutionStatus,
  meta?: any,
}

const SET_SHOULD_NOT_ALLOW_GENERATE = new Set<string>([EDatasetExecutionStatus.IN_PROGRESS, EDatasetExecutionStatus.PENDING])

export const enum EBulkEditPackageProperty {
  DO_NOT_CHANGE = 'doNotChange',
  BULK_EDIT = 'bulkEdit',
}

/**
 * Raw and Single used interchangeably
 */
const CoverageRequirementsTreeComponent = (props: ITreeComponentProps) => {
  const isReadOnlyStudy: boolean| undefined = props?.isReadOnlyStudy

  const emptyTree: ITreeNode = {nodeId: "", name: "", nodeType: ""}
  const dispatch = useAppDispatch()
  const {instance, accounts} = useMsal()

  const selectedCoverageRequirementsDatasetId = useAppSelector(state => selectCoverageRequirementsDataState(state)?.id)
  const coverageRequirementsClipboard = useAppSelector(state => selectCoverageRequirementsClipboard(state))


  const idref = useRef<any>(null)
  idref.current = selectedCoverageRequirementsDatasetId

  /**
   * Tree Node Selection
   */
  const [mergeDeleteFlag, setMergeDeleteFlag] = useState<boolean>(false)
  const [dataSetToDelete, setDataSetToDelete] = useState<ITreeNode[]>([])
  const [rawCheckedNodes, setRawCheckedNodes] = useState<ITreeNode[]>([])
  const [mergedCheckedNodes, setMergedCheckedNodes] = useState<ITreeNode[]>([])
  const refreshCheckedNodesBasedOnTree = (newTree: ITreeNode, checkedNodes: ITreeNode[], treeType: string) => {
    let newCheckedNodesList: ITreeNode[] = [];
    checkedNodes.forEach((node: ITreeNode) => {
      const newNode = getNodeById(newTree, node?.nodeId)
      if (newNode != null) {
        newCheckedNodesList.push(newNode)
      }
    })
    getCheckedNodesStateBasedOnTreeType(treeType)?.[1](newCheckedNodesList)
  }

  /**
   * Execution
   */
  const executionStatusMap: any = useAppSelector(selectDatasetsExecutionStatus)
  const [analysisController, setAnalysisController] = useState<any>({});
  const [isExecutionLoading, setExecutionIsLoading] = useState(false)
  const benchmarkDataRedux = useAppSelector(selectBenchmarkData);

  /**
   * Copying and Pasting Datasets States for Dialogs
   */
  const [isCopyPasteDialogOpen, setIsCopyPasteDialogOpen] = React.useState(false)
  const [isCopyPasteDialogOpenStudies, setIsCopyPasteDialogOpenStudies] = React.useState(false)
  const [modifyData, setModifyData] = useState(null);
  const [includeAnalysis, setIncludeAnalysis] = React.useState(false);
  const [shouldBulkEditPackagePropertyForCopyDatasetToOtherStudy, setShouldBulkEditPackagePropertyForCopyDatasetToOtherStudy] = useState(EBulkEditPackageProperty.DO_NOT_CHANGE);
  const [combinedCoverageRequirementsData, setCombinedCoverageRequirementsData] = useState<any>('');
  const studiesFetchHandle = usePostRequest(coverageRequirementsStudiesDataEndpoint)
  const coverageRequirementsDataFetchHandle = usePostRequest(coverageRequirementsDataEndpoint)
  const [selectedAdasProgramName, setSelectedAdasProgramName] = useState<string>("")
  const [selectedModelYear, setSelectedModelYear] = useState<string>("")
  const [studyMap, setStudyMap] = useState({});

  const WIDTH = 1000

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShouldBulkEditPackagePropertyForCopyDatasetToOtherStudy((event.target as HTMLInputElement).value as EBulkEditPackageProperty);
  };

  const handleCheckboxChange = (event: any) => {
    setIncludeAnalysis(event.target.checked);
  };

  useEffect(() => {
    studiesFetchHandle().then((studiesData) => {
      const map = studiesData.reduce((acc: { [x: string]: any; }, study: { id: string | number; name: any; }) => {
        acc[study.id] = study.name;
        return acc;
      }, {});


      setStudyMap(map);
    }).catch(err => {
      console.log(err);
      toast.error("Failed to load studies data");
    });

    coverageRequirementsDataFetchHandle().then((coverageRequirementsData) => {
      const studyDataArray = coverageRequirementsData.study_data;
      if (Array.isArray(studyDataArray)) {
        const studyIds = Object.keys(studyMap);
        const filteredData = studyDataArray.filter(study =>
          studyIds.includes(study.studyId) && study.treeType === 'Single'
        );
        setCombinedCoverageRequirementsData(filteredData);
      } else {
        console.error('coverageRequirementsData is not an array');
      }
    }).catch(err => {
      console.log(err);
      toast.error("Failed to load coverage analysis data");
    });

  }, [isCopyPasteDialogOpenStudies]);

  useEffect(() => {
    dispatch(setBenchmarkData({concurrentExecutions: rawCheckedNodes.length.toString()}))
  }, [rawCheckedNodes])

  /** Purpose: Call the appropriate durable functions/websocket and generate the selected datasets */
  const generateCheckedDatasets = async () => {
    setExecutionIsLoading(true)

    let localControllerObject: any = {}

    const nodesToBeGenerated = getDatasetIdsForExecution(rawCheckedNodes)

    for (let i = 0; i < nodesToBeGenerated.length; i++) {
      const data={'datasetId':nodesToBeGenerated[i],'executionStatus':EDatasetExecutionStatus.PENDING,
                  'treeType': props.treeType, 'studyId': props.studyId}
      //Imported method executes both panel pending statuses to stay in sync
      handleExecutionStatusUpdate(data, data.datasetId, dispatch)
    }

    setAnalysisController(localControllerObject)

    const idTok = await requestIdToken(instance, accounts);
    let payload: any = {
      "selectedDatasetIds": nodesToBeGenerated,
      "studyId": props.studyId,
      "generator": gmid,

    }

    fetch(generateDatasetsEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "Authorization": `Bearer ${idTok}`
      },
      body: JSON.stringify(payload)
    })
      .then(async (_analysisData) => {
      })
      .catch((e: Error) => {
        console.log(e)
      })
  }

  const fetchHandleCancelRuns = usePostRequest(COVERAGE_REQUIREMENTS_CONSTANTS.details.endpoints.cancelRawRedlineRuns)
  const handleCancelMultipleAnalysis = () => {
    //Do we need to pass on cancel as a prop? or can we just go through the map here?
    let datasetIdsToCancel: string[] = []
    rawCheckedNodes.forEach((node: ITreeNode) => {
      const data: ITreeNode = node;
      if (data?.datasetId != null && IN_PROGRESS_STATUSES.has(data?.executionStatus)) {
        datasetIdsToCancel.push(data?.datasetId)
      }
    })
    fetchHandleCancelRuns({
      studyId: props.studyId,
      datasetIds: datasetIdsToCancel
    }).then((_data) => {
    }).catch((e) => {
      console.log(e)
      toast.error('Failure in cancel runs request')
    })
  }
  const cancellingDatasetsMap = useAppSelector(selectIsCancellingDatasets)

  /**
   * Tree Operations
   */
  const [coverageRequirementsTree, setCoverageRequriementsTree] = useState<any>(props?.treeObjectData.tree)
  const [isTreeDataLoading, setIsTreeDataLoading] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [isDeletingMerge, setIsDeletingMerge] = useState<boolean>(false)

  const getParent = (treeData: ITreeNode, contextNodeId: string, parentId: any): any => {
    if (treeData?.nodeId == contextNodeId) {
      return parentId
    }

    if (treeData?.children != undefined && treeData?.children?.length > 0) {
      for (let i = 0; i < treeData?.children?.length; i++) {
        let child = treeData?.children?.[i];
        let result: any = getParent(child, contextNodeId, treeData?.nodeId)
        if (result !== null) {
          return result;
        }
      }
    }
    return null
  }
  const getParentIdForSourceNode = (contextNodeId: string) => {
    const treeData: ITreeNode = props?.treeObjectData?.tree
    let parentId: any = null;
    parentId = getParent(treeData, contextNodeId, parentId)
    return parentId
  }

  const modifyTreeNode = (newNodeData: any, contextNodeId: string, nodeType: string, action: string) => {
    let _sourceNode: ITreeNode | null = getNodeById(props?.treeObjectData?.tree, newNodeData?.contextNodeId)
    const isActionDuplicate = action == TREE_OPERATIONS.DUPLICATE_DATASET;
    const owner = isActionDuplicate ? gmid : newNodeData['owner']
    const name = isActionDuplicate ? `${_sourceNode?.["name"]}_duplicate` : newNodeData['name']
    const description = isActionDuplicate ? _sourceNode?.["description"] : newNodeData['description']

    let payload: any = {
      "studyId": props.studyId,
      "owner": owner,
      "creator": gmid,
      "name": name,
      "description": description,
      "executionStatus": EDatasetExecutionStatus.NOT_STARTED,

      "treeId": props?.treeObjectData?.id,
      "action": action,
      "parentId": isActionDuplicate ? getParentIdForSourceNode(contextNodeId) : contextNodeId,
      "nodeType": nodeType,
      "data": isActionDuplicate ? {"name": name, "owner": owner, "description": description} : newNodeData,
    }

    if (action == TREE_OPERATIONS.EDIT_DATASET) {
      payload["nodeId"] = contextNodeId
      payload["datasetId"] = contextDatasetId
    }
    if (action == TREE_OPERATIONS.DUPLICATE_DATASET) {
      payload["sourceDatasetId"] = contextDatasetId
      payload["includeAnalysisResults"] = newNodeData['includeAnalysisResults']
    }
    setIsTreeDataLoading(true)
    toast.loading(`Keep this tab open. Don't modify the study. Modifying tree node. action: ${action}.`, {id: 'fetchHandleModifyNode'})
    fetchHandleModifyNode(payload)
      .then((_data) => {
        // setCoverageRequriementsTree(data[0]["tree"])
        props.fetchStudy()
        setIsTreeDataLoading(false)

        if (action == TREE_OPERATIONS.EDIT_DATASET && contextNodeId == lastSelectedItem)
          props.setSeed((prevState: any) => prevState + 1);
      }).catch((e) => {
      console.log(e)
      toast.error('Failed to add node.')
      setIsTreeDataLoading(false)
    }).finally(() => {
      toast.dismiss('fetchHandleModifyNode')
    })
  }
  /**
   * Deletes node and update list of selected tree nodes
   *
   */
  const deleteNodes = (checkedNodes: ITreeNode[], treeType: string) => {
    setIsTreeDataLoading(true)
    if (mergeDeleteFlag)
      setIsDeletingMerge(true)
    else
      setIsDeleting(true)

    const filteredNodesThatHaveFolderChecked = checkedNodes.filter((node: ITreeNode) => node?.nodeType == 'folder')

    const filteredNodesThatHaveDatasetChecked = checkedNodes.filter((node: ITreeNode) => node?.nodeType == 'dataset')

    const datasetFetchRequest = filteredNodesThatHaveDatasetChecked.map((element: any) => ({
      "nodeToDelete": element.nodeId,
      "action": "delete",
      "datasetId": element.datasetId,
      "nodeType": element.nodeType,
      "treeId": props.treeObjectData.id,
      "gmid": gmid
    }))

    const foldersFetchRequest = filteredNodesThatHaveFolderChecked.map((element: any) => ({
      "nodeToDelete": element.nodeId,
      "action": "delete",
      "nodeType": element.nodeType,
      "treeId": props.treeObjectData.id,
      "gmid": gmid
    }))

    // Fetch handle returns updated tree without the deleted node
    fetchHandleModifyNode({
      "nodeToDelete": [...datasetFetchRequest, ...foldersFetchRequest],
      "action": "delete",
      "treeId": props.treeObjectData.id,
      'treeType': treeType,
      "studyId": props.studyId
    }).then((data) => {
      let checkedNodes: ITreeNode[] = getCheckedNodesStateBasedOnTreeType(props.treeType)?.[0] ?? []
      // update checked items if the delted node was checked
      if (contextDatasetType === SINGLE_DATASET_TYPE_STR) {
        const newRawCheckedNodeIds = []
        for (let i = 0; i < checkedNodes.length; i++) {
          const rawCheckedNode = checkedNodes[i]
          if (rawCheckedNode.datasetId != contextDatasetId) {
            newRawCheckedNodeIds.push(rawCheckedNode)
          }
        }
        getCheckedNodesStateBasedOnTreeType(props.treeType)?.[1](newRawCheckedNodeIds)
        // setCoverageRequriementsTree(data[0]["tree"])
      } else {
        // setCoverageRequriementsTree(data[0]["tree"])
      }
      props.fetchStudy()
      setSelectedDatasetId(undefined)
      setIsTreeDataLoading(false)
      if (mergeDeleteFlag)
        setIsDeletingMerge(false)
      else
        setIsDeleting(false)
    }).catch((e) => {
      console.error(e)
      toast.error('failed to delete node')
      setIsTreeDataLoading(false)
      if (mergeDeleteFlag)
        setIsDeletingMerge(false)
      else
        setIsDeleting(false)
    })
  }
  /**
   * Delete node and update list of selected tree nodes
   * @param nodeIdToDelete
   *
   * @param treeType SINGLE_DATASET_TREE_TYPE or MERGED_DATASET_TREE_TYPE
   */
  const deleteNode = (nodeIdToDelete: string, treeType: string) => {

    setIsTreeDataLoading(true)
    if (mergeDeleteFlag)
      setIsDeletingMerge(true)
    else
      setIsDeleting(true)
    // Fetch handle returns updated tree without the deleted node
    fetchHandleModifyNode({
      "nodeToDelete": nodeIdToDelete,
      "action": "delete",
      "datasetId": contextDatasetId,
      "nodeType": contextDatasetId === "" ? "folder" : "dataset",
      "treeId": contextTreeId,
      'treeType': treeType,
      "gmid": gmid,
      studyId: props.studyId
    }).then((data) => {
      let checkedNodes: ITreeNode[] = getCheckedNodesStateBasedOnTreeType(treeType)?.[0] ?? []
      // update checked items if the deleted node was checked
      if (contextDatasetType === SINGLE_DATASET_TYPE_STR) {
        const newRawCheckedNodeIds = []
        for (let i = 0; i < checkedNodes.length; i++) {
          const rawCheckedNode = checkedNodes[i]
          if (rawCheckedNode.datasetId != contextDatasetId) {
            newRawCheckedNodeIds.push(rawCheckedNode)
          }
        }
        getCheckedNodesStateBasedOnTreeType(treeType)?.[1](newRawCheckedNodeIds)
      } else {
      }
      props.fetchStudy()
      setSelectedDatasetId(undefined)
      setIsTreeDataLoading(false)
      if (mergeDeleteFlag)
        setIsDeletingMerge(false)
      else
        setIsDeleting(false)

    }).catch((e) => {
      console.error(e)
      toast.error('failed to delete node')
      setIsTreeDataLoading(false)
      if (mergeDeleteFlag)
        setIsDeletingMerge(false)
      else
        setIsDeleting(false)
    })
  }
  const switchIdIfSelectedNodeDeleted = (data: any) => {
    // TODO: revist when right panel connected
    const currentIds = getCurrentIdsFromTree(data, []);
    if (currentIds.length == 0) {
      setSelectedDatasetId(undefined)
    } else if (currentIds.indexOf(selectedDatasetId) == -1) {
      // TODO: root.children[0]?
      setSelectedDatasetId(currentIds[0])
      setSelectedDatasetNodeId(data.children[0].nodeId)
      // TODO: setSelectedDatasetName
      setSelected(currentIds[0])
    }
  }
  const getCurrentIdsFromTree = (tree: any, idsList: any[]) => {
    if (tree.nodeType == "dataset") {
      idsList.push(tree.datasetId)
      return idsList;
    }
    tree.children.forEach((child: any) => {
      idsList.concat(getCurrentIdsFromTree(child, idsList))
    })
    return idsList;
  }
  const fetchHandleModifyNode = usePostRequest(coverageRequirementsModifyTreeEndpoint)
  const networkHandleCopyPaste = usePostRequest(coverageRequirementsCopyDataEndpoint)
  const networkHandleCopyPasteToStudy = usePostRequest(coverageRequirementsCopyDataToStudyEndpoint)

  // const clipboardData = useAppSelector(selectCoverageRequirementsClipboard)
  const copyNodeToStudy = () => {

    // source and target node datta
    const copyToStudyData = coverageRequirementsClipboard?.copyToStudyData
    // nodeToCopy data
    const nodeToCopy = coverageRequirementsClipboard?.nodeToCopy

    // adas program
    let adasProgramItemData: any = {}
    adasProgramsData.forEach((element: any) => {
      if (element?.data?.programName === selectedAdasProgramName && element?.data?.modelYear === selectedModelYear) {
        adasProgramItemData = element
      }
    })
    const adasProgramId = adasProgramItemData.adasProgramId

    const payload = {
      copyToStudyData,
      adasProgramId,
      includeAnalysis,
      selectedOption: shouldBulkEditPackagePropertyForCopyDatasetToOtherStudy,
      nodeToCopy,
    }

    toast.loading("Keep this tab open. Don't modify the study. Copying node to study.", {id: 'copyNodeToStudy'})
    // TODO: Make network call to do the copy
    networkHandleCopyPasteToStudy(payload).then((data) => {
      toast.success('Node copied to study')
    }).catch((e) => {
      console.error(e)
      toast.error('Please select a Destination Study or Reduce Size of Contents Copied')
    }).finally(() => {
        toast.dismiss('copyNodeToStudy')
    })
    // close popup
    setIsCopyPasteDialogOpenStudies(false)
  }

  const treeOperation = (operationType: string, data: object) => {
    const copyToStudyData = {
      sourceStudyId: props.studyId,
      sourceNodeId: contextNodeId,
    }
    switch (operationType) {
      case TREE_OPERATIONS_SWITCH_OPTIONS.ADD_NEW_FOLDER:
        modifyTreeNode(data, contextNodeId, "folder", TREE_OPERATIONS.ADD)
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.EDIT_FOLDER:
        modifyTreeNode(data, contextNodeId, "folder", TREE_OPERATIONS.EDIT_FOLDER)
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.ADD_NEW_DATASET:
        modifyTreeNode(data, contextNodeId, "dataset", TREE_OPERATIONS.ADD)
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.EDIT_DATASET:
        modifyTreeNode(data, contextNodeId, "dataset", TREE_OPERATIONS.EDIT_DATASET)
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.DUPLICATE_DATASET:
        modifyTreeNode(data, contextNodeId, "dataset", TREE_OPERATIONS.DUPLICATE_DATASET)
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.DELETE:
        deleteNode(contextNodeId, props.treeType)
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.COPY_FOLDER:
        dispatch(setCoverageRequirementsClipboard({...data, "itemType": "folder", copyToStudyData}))
        toast.success("Added Folder to Clipboard!")
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.COPY_FOLDER_TO_STUDY:
        dispatch(setCoverageRequirementsClipboard({...data, "itemType": "folder", copyToStudyData}))
        toast.success("Added Folder to Clipboard!")
        setIsCopyPasteDialogOpenStudies(true);
        fetchPackageData();
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.COPY_DATASET:
        dispatch(setCoverageRequirementsClipboard({...data, "itemType": "dataset", copyToStudyData}))
        toast.success("Added Dataset to Clipboard!")
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.COPY_DATASET_TO_STUDY:
        dispatch(setCoverageRequirementsClipboard({...data, "itemType": "dataset", copyToStudyData}))
        toast.success("Added Dataset to Clipboard!")
        setIsCopyPasteDialogOpenStudies(true);
        fetchPackageData();
        break;
      case TREE_OPERATIONS_SWITCH_OPTIONS.PASTE_CONTENTS:
        const modifyData = {
          ...data,
          ...coverageRequirementsClipboard
        };
        setModifyData(modifyData);
        setIsCopyPasteDialogOpen(true);
        break;
      default:
        break;
    }
  }

  const handleCopyPaste = (newNodeData: any, contextNodeId: string, nodeType: string, action: string) => {
    // TODO: add datasetName to url and label, and add study name to label (decode studyId?)
    let _sourceNode: ITreeNode | null = getNodeById(props?.treeObjectData?.tree, newNodeData?.contextNodeId)
    // TODO if not used anywhere, delete add parameter
    const currentDateTime = new Date()
    const isActionDuplicate = action == TREE_OPERATIONS.DUPLICATE_DATASET;
    const owner = isActionDuplicate ? _sourceNode?.["owner"] : newNodeData['owner']
    const name = isActionDuplicate ? `${_sourceNode?.["name"]}_duplicate` : newNodeData['name']
    const description = isActionDuplicate ? _sourceNode?.["description"] : newNodeData['description']

    let payload: any = {
      "studyId": props.studyId,
      "owner": owner,
      "creator": gmid,
      "name": name,
      "description": description,
      "executionStatus": EDatasetExecutionStatus.NOT_STARTED,
      "date": currentDateTime,
      "includeAnalysis": includeAnalysis,

      "treeId": props?.treeObjectData?.id,
      "action": action,
      "parentId": isActionDuplicate ? getParentIdForSourceNode(contextNodeId) : contextNodeId,
      "nodeType": nodeType,
      "data": isActionDuplicate ? {"name": name, "owner": owner, "description": description} : newNodeData,
    }

    // request_operation
    // request_type
    if (action == TREE_OPERATIONS.EDIT_DATASET) {
      payload["nodeId"] = contextNodeId
      payload["datasetId"] = contextDatasetId
    }
    if (action == TREE_OPERATIONS.DUPLICATE_DATASET) {
      payload["sourceDatasetId"] = contextDatasetId
      payload["includeAnalysisResults"] = newNodeData['includeAnalysisResults']
    }
    setIsTreeDataLoading(true)

    toast.loading("Keep this tab open. Don't modify the study. Pasting contents.", {id: 'handleCopyPaste'})
    networkHandleCopyPaste(payload)
      .then(() => {
        setIsTreeDataLoading(false)

        if (action == TREE_OPERATIONS.EDIT_DATASET && contextNodeId == lastSelectedItem)
          props.setSeed((prevState: any) => prevState + 1);
      }).catch((e) => {
      console.log(e)
      toast.error('Failed to add node.')
      setIsTreeDataLoading(false)
    }).finally(() => {
        toast.dismiss('handleCopyPaste')
    })
  }
  /**
   * Users data
   */
  const gmid = useAppSelector(state => selectCurrentUser(state).id)
  const hasWriteAccess = useHasWriteAccess()
  const isUserAdmin = useGetIsUserAdmin()

  /**
   * Favorites functionality
   */
  const fromDialog = useAppSelector(state => selectGlobalDialogState(state).isOpen)

  /**
   * Context menu (when right click tree item) state
   */
  const [contextMenuOptions, setContextMenuOptions] = useState<any>([])
  const [isTreeNodeContextMenuOpen, setIsTreeNodeContextMenuOpen] = useState(false)
  const [contextMenuTopLeftCoordinates, setContextMenuTopLeftCoordinates] = useState({x: 0, y: 0})
  const [contextNodeId, setContextOpenNode] = useState<string>("")
  const [contextDatasetId, setContextDatasetId] = useState<string | undefined>("")
  const [contextDatasetType, setContextDatasetType] = useState<DatasetType>("")
  const [contextTreeId, setContextTreeId] = useState<string>("")
  const [isContextMenuTreeNodeDataset, setIsContextMenuTreeNodeDataset] = useState(false)

  useEffect(() => {
    setCoverageRequriementsTree(props?.treeObjectData?.tree)
    let checkedNodes = getCheckedNodesStateBasedOnTreeType(props?.treeType)?.[0] ?? []
    refreshCheckedNodesBasedOnTree(props?.treeObjectData?.tree, checkedNodes, props?.treeType)
  }, [props?.treeObjectData.tree])

  const getCheckedNodesStateBasedOnTreeType = (treeType: string): any => {
    if (treeType == SINGLE_DATASET_TREE_TYPE)
      return [rawCheckedNodes, setRawCheckedNodes]
    else if (treeType == MERGED_DATASET_TREE_TYPE)
      return [mergedCheckedNodes, setMergedCheckedNodes]
  }

  /**
   * Selected datasets
   */
  const [selectedDatasetId, setSelectedDatasetId] = useQueryParam(SELECTED_DATASET_ID_URL_PARAM, StringParam)
  const [selectedDatasetNodeId, setSelectedDatasetNodeId] = useState<string>("")
  const [selected, setSelected] = useState<any>("")

  /**
   * Path to selected tree node
   */
  const defaultRawPath = useRef<string[]>()
  const [selectedDatasetPath, setSelectedDatasetPath] = useState<string[]>([])

  /**
   * Update the expanded node list on clicking tree items
   */
  const handleToggle = (event: any, nodeIds: any) => {
    if (event.target.closest(".MuiTreeItem-iconContainer")) {
      /* Only update expanded nodes when clicking the expand/collapse icon */
      setSelectedDatasetPath(nodeIds)
    }
  }

  const setSelectedAndPath = () => {
    if (defaultRawPath.current == null) {
      defaultRawPath.current = findNodePath(props?.treeObjectData?.tree, SELECTED_DATASET_ID_URL_PARAM, selectedDatasetId ?? '')
    }
    let _path = defaultRawPath.current
    let _selected = ''
    if (!selectedDatasetNodeId) {
      _selected = findNodeId(coverageRequirementsTree, selectedDatasetId ?? '')
    } else {
      _selected = selectedDatasetNodeId
    }
    setSelected(_selected)
    if (selectedDatasetPath.length == 0)
      setSelectedDatasetPath(_path ?? [])
  }
  useEffect(() => {
    setSelectedAndPath()
  }, [selectedDatasetNodeId])

  useEffect(() => {
    // set recently viewed
    if (selectedDatasetId != null && selectedDatasetId?.length > 0 && gmid != null) {
      // setRecentlyViewed(selectedDatasetId)
      const treeData = coverageRequirementsTree
      const nodeId = getNodeIdByDatasetId(treeData, selectedDatasetId)?.nodeId
      const path = pathNodesToNames(getPathToNode(nodeId, treeData), treeData)
      const pathWithStudyName = path
      path.shift()
      props.setDatasetPath(path)
    }
  }, [selectedDatasetId, props.treeObjectData, coverageRequirementsTree])


  const selectedMergeSetExecutionStatus = useAppSelector(selectMergeSetsLocalState)
  /**
   *  Disable "Regenerate Outdated" button if no datasets are selected and if no merges are currently happening
   */
  useEffect(() => {
    setCanRegenerate(mergedCheckedNodes.length !== 0 && !checkIfMergeIsHappening())
  }, [mergedCheckedNodes, selectedMergeSetExecutionStatus])

  /**
   * Merging
   */
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false)
  const [canRegenerate, setCanRegenerate] = useState<boolean>(false)
  const [mergedDatasetsTreeData, setMergedDatasetsTreeData] = useState<ITreeNode>(props?.treeObjectData?.tree ?? emptyTree)
  const [leftSidebarTabIndex, setLeftSidebarTabIndex] = useQueryParam(COVERAGE_REQUIREMENTS_HORIZONTAL_TABS_TWO_NAME_URL_PARAM, StringParam)
  const mergeDatasetsRegenerateHandle = usePostRequest(mergeDatasetsUpdateEndpoint)

  /**
   * Merge datasets dialog state
   */
  const [isMergeDatasetsDialogOpen, setIsMergeDatasetsDialogOpen] = useState<boolean>(false)
  const [isMerging, setIsMerging] = useState<boolean>(false)
  const [regenerateSummary, setRegenerateSummary] = useState<any>(null)
  const onClickMerge = () => {
    if (isMerging)
      return;

    setIsMergeDatasetsDialogOpen(true)
  }

  /**
   * Return whether a tree node is the study root node or not
   *
   * @param node
   * @returns boolean
   */
  const isRootNode = (node: ITreeNode) => {
    if (node?.nodeType && node?.nodeType == "study") {
      return true
    }
    return false
  }

  const handleCheckRegenMerge = usePostRequest(checkRegenMergeEndpoint)

  const [mergedSetsToBeDeleted, setMergedSetsToBeDeleted] = useState<any>(null)
  const regenerateOutdated = (isChecking: boolean) => {
    // for each item in mergedCheckedNodes list, make a new list without the children key
    let checkedNodesWithoutChildren: ITreeNode[] = []
    mergedCheckedNodes.forEach((node: ITreeNode) => {
      // Filter out the study root node from the checked nodes list
      if (!isRootNode(node)) {
        let nodeWithoutChildren = {...node}
        delete nodeWithoutChildren.children
        checkedNodesWithoutChildren.push(nodeWithoutChildren)
      }
    })

    const selectedMergedDatasetSetNodesForRegenerate = checkedNodesWithoutChildren
    setIsRegenerating(true)

    const search = window.location.search
    const o: any = queryString.parse(search)
    const stringifiedUrlParams = queryString.stringify(o)
    let trackedItemUrl = `${coverageRequirementsDetails}?${stringifiedUrlParams}`
    trackedItemUrl = encodeURI(trackedItemUrl)
    if (isChecking) {
      handleCheckRegenMerge({
        "mode": MERGE_MODES.UPDATE,
        "studyId": props?.studyId,
        "studyName": props?.studyName,
        "generator": gmid,
        isChecking,
        "selectedMerges": selectedMergedDatasetSetNodesForRegenerate
      }).then((data) => {
        if (data?.op === 'regen')
          regenerateOutdated(false)
        // Orignal raw dataset changed or not outdated show message and do not allow to regenerate
        else if (data?.op === 'origUpOrOutdated'){
          toast.error( (t) => (
            <span>
            {data["errorMessage"]}
            <ArgosButton
                variant="text"
                fontSize={11.5}
                onClick={() => toast.remove(t.id)}>Dismiss
            </ArgosButton>
            </span>
          ),
          {
            duration: Infinity,
            id: "error-regen"
          })
        }
        else
          setMergedSetsToBeDeleted(data?.mergedSetsToBeDeleted)
      }).catch((e) => {
        toast.error(`Error checking outdated merged ${VAR_COMMON_CONSTANTS.VAR_DATA_SETS}`)
      }).finally(() => {
        setIsRegenerating(false)
      })
    } else {
      mergeDatasetsRegenerateHandle({
        "mode": MERGE_MODES.UPDATE,
        "studyId": props?.studyId,
        "studyName": props?.studyName,
        "generator": gmid,
        isChecking,
        "selectedMerges": selectedMergedDatasetSetNodesForRegenerate
      }).then((data) => {
        if (data?.mergedSetsToBeDeleted) {
          // setMergedSetsToBeDeleted(data?.mergedSetsToBeDeleted)
        } else {
          const mapMergedSetNameToNewDeletedUpdatedNames = data?.mapMergedSetNameToNewDeletedUpdatedNames
          if (mapMergedSetNameToNewDeletedUpdatedNames) {
            setRegenerateSummary(mapMergedSetNameToNewDeletedUpdatedNames)
          }
        }
        props.fetchStudy()
      }).catch((e) => {
        console.log(e)
        toast.error(`Error regenerating outdated merged ${VAR_COMMON_CONSTANTS.VAR_DATA_SETS}`)
      }).finally(() => {
        setIsRegenerating(false)
      })
    }
  }

  /**
   * RedlineAppDialog state
   */
  const [isRedlineAppDialogOpen, setIsRedlineAppDialogOpen] = useState<boolean>(false)
  const [redlineAppDialogTitle, setRedlineAppDialogTitle] = useState<string>("")
  const [redlineAppDialogType, setRedlineAppDialogType] = useState<string>("")
  const [isDeleteAppDialogOpen, setIsDeleteAppDialogOpen] = useState<boolean>(false)

  function handleOpenRedlineAppDialog(dialogType: string, dialogTitle: string) {
    setIsRedlineAppDialogOpen(!(dialogType === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_FOLDER ||
      dialogType === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_DATASET ||
      dialogType === TREE_OPERATIONS_SWITCH_OPTIONS.PASTE_CONTENTS ||
      dialogType === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_DATASET_TO_STUDY ||
      dialogType === TREE_OPERATIONS_SWITCH_OPTIONS.COPY_FOLDER_TO_STUDY));
    setRedlineAppDialogType(dialogType);
    setRedlineAppDialogTitle(dialogTitle);

  }

  function handleCloseRedlineAppDialog() {
    setIsRedlineAppDialogOpen(false);
  }

  function handleCloseDeleteAppDialog() {
    setIsDeleteAppDialogOpen(false);
  }

  /**
   * Users data
   */
  const [userData, setUserData] = useState<User[]>([])
  const [userIdNameMap, setUserIdNameMap] = useState<any>({})
  const usersFetchHandle = usePostRequest(usersDataEndpoint)
  const fetchUsersData: any = () => {
    /**
     * Optimistic fetch for users data, used after right click tree node.
     */
    // Used to get users data for owner drop-down selects
    usersFetchHandle().then((data) => {
      const usersData = data.filter(
        (element: any) =>
          hasThisAccessLevelToRouteKey(EArgosRouteKeys.COVERAGE_REQUIREMENTS, element, EUserPermissionAccess.READ_WRITE)
      )
      const userIdNamesObj = usersData.map((elem: User) => {
        return {
          [elem.id]: elem.firstName + " " + elem.lastName
        }
      })
      const userIdNames = Object.assign({}, ...userIdNamesObj)
      setUserData(usersData)
      setUserIdNameMap(userIdNames)
    }).catch(err => {
      console.log(err)
      toast.error("Failed to load users data")
    })
  }
  useEffect(() => {
    fetchUsersData()
  }, [])

  /**
   * On page load, auto scroll to selected tree node (from url).
   */
  const selectedTreeNodeRef = useRef<any>()
  useEffect(() => {
    if (selectedTreeNodeRef.current != null) {
      const node = selectedTreeNodeRef.current
      if (node != null) {
        node.scrollIntoView({behavior: 'smooth', block: 'center'})
      }
    }
  }, [selectedTreeNodeRef.current])

  /**
   * Tree node path helpers
   */
  const getPathsToAllNodes: any = (nodes: ITreeNode) => {
    return (
      nodes.children == undefined || nodes.children.length == 0) ? [[nodes.nodeId]]
      : nodes.children.flatMap(getPathsToAllNodes).map((r: any) => [nodes.nodeId, ...r]
      )
  }
  const getPathToNode: any = (nodeId: any, tree: any) => getPathsToAllNodes(tree).filter((r: any) => r[r.length - 1] == nodeId)[0]

  function pathNodesToNames(path: string[], tree: ITreeNode) {
    let res: string[] = []
    path?.forEach((nodeId) => {
      const name = nodeIdToName(tree, nodeId)
      if (name != null) {
        res.push(name)
      }
    })
    return res
  }


  /**
   * Node helpers
   */

  function nodeIdToName(nodes: ITreeNode, id: string) {
    const node = getNodeById(nodes, id)
    if (node != null) {
      const name = node.name
      return name
    }
    return null
  }

  function getChildById(node: ITreeNode, id: string) {
    let array: ITreeNode[] = []

    function getAllChild(nodes: ITreeNode | null) {
      if (nodes === null) return []
      array.push(nodes)
      if (Array.isArray(nodes.children)) {
        nodes.children.forEach(node => {
          array = [...array, ...getAllChild(node)]
          array = array.filter((v, i) => array.indexOf(v) === i)
        })
      }
      return array;
    }

    return getAllChild(getNodeById(node, id))
  }

  const checkIfMergeIsHappening = React.useCallback(() => {
    const mergeSetKeys = Object.keys(selectedMergeSetExecutionStatus ?? {})
    for (let i = 0; i < mergeSetKeys.length; i++) {
      if (
          selectedMergeSetExecutionStatus?.[mergeSetKeys[i]]?.status === EDatasetExecutionStatus.IN_PROGRESS ||
          selectedMergeSetExecutionStatus?.[mergeSetKeys[i]]?.status === EDatasetExecutionStatus.PENDING
      ) {
        return true
      }
    }
    return false
  }, [selectedMergeSetExecutionStatus])

  /**
   * @param nodes Selected menu item.
   * @param treeType
   * @returns A list of menu options for a dataset, study, or folder.
   */
  const getContextMenuItem = React.useCallback(
      (nodes: ITreeNode, treeType: any) => {
        if (isReadOnlyStudy) {
          return []
        }
        if (treeType == SINGLE_DATASET_TREE_TYPE) {
          switch (nodes?.nodeType) {
            case "dataset":
              if (canDeleteItem(nodes, gmid, isUserAdmin)) {
                return nodes?.executionStatus && SET_SHOULD_NOT_ALLOW_GENERATE.has(nodes?.executionStatus) ? [] : contextMenuRawDataset
              } else if (hasWriteAccess) {
                return contextMenuRawDataset?.filter(
                    (item: any) => item.type !== "delete" && item.type !== "editDataset"
                )
              }
              return []
            case "study":
              return contextMenuRawStudy
            case "folder":
              if (canDeleteItem(nodes, gmid, isUserAdmin)) {
                return SET_SHOULD_NOT_ALLOW_GENERATE.has(getTreeItemColor(nodes).executionStatus)
                    ? contextMenuRawFolder?.filter(
                        (item: any) => item.type !== "delete" && item.type !== "copyFolder"
                    )
                    : contextMenuRawFolder
              } else if (hasWriteAccess) {
                return contextMenuRawFolder?.filter(
                    (item: any) => item.type !== "delete"
                )
              }
              return []
          }
        }
        if (checkIfMergeIsHappening()) {
          return []
        }
        switch (nodes?.nodeType) {
          case "study":
            return []
          case "folder":
            if (nodes?.meta?.type == "root") {
              return contextMenuMergeSet
            } else {
              return contextMenuMergedFolder
            }
          case "dataset":
            return contextMenuMergedDataset
        }
      },
      [
        isReadOnlyStudy,
        SINGLE_DATASET_TREE_TYPE,
        canDeleteItem,
        gmid,
        isUserAdmin,
        SET_SHOULD_NOT_ALLOW_GENERATE,
        contextMenuRawDataset,
        hasWriteAccess,
        contextMenuRawStudy,
        getTreeItemColor,
        contextMenuRawFolder,
        checkIfMergeIsHappening,
        contextMenuMergeSet,
        contextMenuMergedFolder,
        contextMenuMergedDataset
      ]
  )


  const getNodeIdByDatasetId = (nodes: ITreeNode, datasetId: any) => {
    if (nodes?.datasetId === datasetId) {
      return nodes
    } else if (Array.isArray(nodes.children)) {
      let result = null;
      nodes.children.forEach(node => {
        if (!!getNodeIdByDatasetId(node, datasetId)) {
          result = getNodeIdByDatasetId(node, datasetId)
        }
      });
      return result
    }
    return null
  }
  const getIsNodeInClipboard = React.useCallback(
      (nodes: ITreeNode) => {
        if (coverageRequirementsClipboard === null)
          return false
        const nodesToCopy = coverageRequirementsClipboard?.nodeToCopy
        if (typeof nodesToCopy == "string")
          return (nodes.nodeId as unknown as string) === nodesToCopy
        return nodesToCopy?.indexOf(nodes?.nodeId as unknown as string) !== -1
      },
      [coverageRequirementsClipboard]
  )
  const datasetsLocalState = useAppSelector(selectDatasetsLocalState)


  /**
   * Helpers for showing owner label in tree node
   */
  const getOwnerNameFromNode = React.useCallback((node: ITreeNode) => {
    if (node.owner && node.owner in userIdNameMap) {
      return userIdNameMap[node.owner]
    }
    return null
  }, [userIdNameMap])

  const getOwnerLabelFromNode = React.useCallback((node: ITreeNode) => {
    const ownerName = getOwnerNameFromNode(node)
    return ownerName ? `(${ownerName})` : null
  }, [getOwnerNameFromNode])
  const getOwnerLabelJsx = React.useCallback(
      (node: ITreeNode, _isCheckedDisplayOwner: boolean) => {
        const ownerLabel = getOwnerLabelFromNode(node)
        return (
            <>
              {
                  ownerLabel && _isCheckedDisplayOwner && <div>
                    <Typography>
                      {ownerLabel}
                    </Typography>
                  </div>
              }
            </>
        )
      },
      [getOwnerLabelFromNode]
  )

  const [isCheckedDisplayOwner, setIsCheckedDisplayOwner] = useState<boolean>(false)
  const onCheckDisplayOwner = (e: any) => {
    setIsCheckedDisplayOwner(e.target.checked)
  }

  /**
   * Rendering tree
   */
  const renderTreeItemLabel = React.useCallback((nodes: ITreeNode) => {
    const isNodeInClipboard = getIsNodeInClipboard(nodes)
    const boxHeight = 40
    switch (nodes.nodeType) {
      case 'study':
        return (
            <ArgosFlexbox style={{height: boxHeight, alignItems: "center", width: "100%"}}>
              <ArgosStudyIcon/>
              <Typography style={{width: "100%", fontWeight: isNodeInClipboard ? "bold" : "normal"}}>
                {nodes.name}
              </Typography>
            </ArgosFlexbox>)
      case 'folder':
        if (nodes.meta?.type == "root") {
          let statusColor = ''
          let labelText = nodes.name
          const mergeSetId = nodes.nodeId
          const currentMergeSetState = selectedMergeSetExecutionStatus?.[mergeSetId ?? '']
          const showProgress = mergeSetId && currentMergeSetState != null && currentMergeSetState?.status === EDatasetExecutionStatus.IN_PROGRESS
          const pendingFlag = currentMergeSetState?.status === EDatasetExecutionStatus.PENDING

          const tooltipComp = (
              <div>
                {currentMergeSetState?.currentIteration} / {currentMergeSetState?.totalIterations}
              </div>
          )

          switch (true) {
            case pendingFlag:
              statusColor = ARGOS_PENDING_COLOR;
              break;
            case showProgress:
              statusColor = ARGOS_PROGRESS_COLOR;
              break;
            case currentMergeSetState?.status === EDatasetExecutionStatus.COMPLETED:
              statusColor = ARGOS_GREEN;
              break;
          }

          const label = (
              <ArgosFlexbox colGap={5}>
                {showProgress &&
                    <ArgosFlexbox colGap={5}>
                      <div>
                        <Typography>
                          {`(${currentMergeSetState?.percentage}%)`}
                        </Typography>
                      </div>
                      <div>
                        <Tooltip placement={'top'} title={tooltipComp}>
                          <InfoIcon/>
                        </Tooltip>
                      </div>
                    </ArgosFlexbox>
                }

                {getOwnerLabelJsx(nodes, isCheckedDisplayOwner)}

                <div>
                  <Typography>
                    {labelText}
                  </Typography>
                </div>
                {pendingFlag && <>
                  <div>
                    <Typography>
                      &nbsp;
                    </Typography>
                  </div>
                  <div>
                    <Loader loaderSize={15} isLoading={true}/>
                  </div>
                  <div>
                    <Typography>
                      <i>{"(Merge " + EDatasetExecutionStatus.PENDING + ")"}</i>
                    </Typography>
                  </div>
                </>
                }
              </ArgosFlexbox>
          )
          return (
              <ArgosFlexbox style={{height: boxHeight, alignItems: "center", color: statusColor}} ref={(el) => {
                if (selectedTreeNodeRef.current == null)
                  selectedTreeNodeRef.current = el
              }}>
                <ArgosFolderIcon/>
                {label}
              </ArgosFlexbox>
          )
        } else {
          return (
              <ArgosFlexbox style={{height: boxHeight, alignItems: "center"}}>
                <ArgosFolderIcon/>
                <Typography style={{fontWeight: isNodeInClipboard ? "bold" : "normal"}}>
                  {nodes.name}
                </Typography>
              </ArgosFlexbox>
          )
        }

      case 'dataset':
        let progress = ''
        let labelText = nodes.name

        const nodeDatasetId = nodes.datasetId
        const currentDatasetState = datasetsLocalState?.[nodeDatasetId ?? '']

        const showProgress = nodeDatasetId && IN_PROGRESS_STATUSES.has(nodes.executionStatus) && currentDatasetState != null
        const pendingFlag = nodes.executionStatus === EDatasetExecutionStatus.PENDING

        if (showProgress && !pendingFlag) {
          progress = currentDatasetState?.progress
        }

        const tooltipComp = (
            <div>
              {currentDatasetState?.fraction}
            </div>
        )
        const label = (
            <ArgosFlexbox colGap={5}>
              {showProgress && !pendingFlag &&
                  <ArgosFlexbox colGap={5}>
                    <div>
                      <Typography>
                        {`(${progress}%)`}
                      </Typography>
                    </div>
                    <div>
                      <Tooltip placement={'top'} title={tooltipComp}>
                        <InfoIcon/>
                      </Tooltip>
                    </div>
                  </ArgosFlexbox>
              }

              {getOwnerLabelJsx(nodes, isCheckedDisplayOwner)}

              <div>
                <Typography style={{fontWeight: isNodeInClipboard ? "bold" : "normal"}}>
                  {labelText}
                </Typography>
              </div>
            </ArgosFlexbox>
        )

        return (
            <ArgosFlexbox style={{height: boxHeight, alignItems: "center"}} ref={(el) => {
              if (selectedTreeNodeRef.current == null)
                selectedTreeNodeRef.current = el
            }}>
              <ArgosDatasetIcon/>
              {label}
            </ArgosFlexbox>)
      default:
        return (
            <ArgosFlexbox style={{height: boxHeight, alignItems: "center"}}>
              <Typography>
                {nodes.name}
              </Typography>
            </ArgosFlexbox>)
    }
  }, [
    getIsNodeInClipboard,
    selectedMergeSetExecutionStatus,
    isCheckedDisplayOwner,
    getOwnerLabelJsx,
    datasetsLocalState,
    selectedTreeNodeRef,
  ])

  const renderTree = (nodes: ITreeNode, treeType: string, _treeObjectData: any) => {
    return (
      <>
        {
          treeType == SINGLE_DATASET_TREE_TYPE ?
            <CustomTreeItem
              nodes={nodes}
              treeType={treeType}
              _treeData={_treeObjectData}
              renderTreeItemLabel={renderTreeItemLabel}
              treeNodeOnClick={treeNodeOnClick}
              treeNodeOnContextMenu={treeNodeOnContextMenu}
              renderTree={renderTree}
              checkedNodes={rawCheckedNodes}
              onChangeChangedNodes={handleRawCheckedNodes}
            />
            :
            <CustomTreeItem
              nodes={nodes}
              treeType={treeType}
              _treeData={_treeObjectData}
              renderTreeItemLabel={renderTreeItemLabel}
              treeNodeOnClick={treeNodeOnClick}
              treeNodeOnContextMenu={treeNodeOnContextMenu}
              renderTree={renderTree}
              checkedNodes={mergedCheckedNodes}
              onChangeChangedNodes={handleMergedCheckedNodes}
            />
        }
      </>
    )
  }

  const [treeContextNode, setTreeContextNode] = useState<ITreeNode>(emptyTree)

  const openContextMenu = React.useCallback((e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setIsTreeNodeContextMenuOpen(true);
    setContextMenuTopLeftCoordinates({
      x: e.pageX,
      y: e.pageY
    });
  }, [setIsTreeNodeContextMenuOpen, setContextMenuTopLeftCoordinates]);

  const treeNodeOnContextMenu = React.useCallback(
      (e: any, node: ITreeNode, treeType: string, _treeData: any, datasetType: DatasetType) => {
        openContextMenu(e)
        setContextOpenNode(node.nodeId);
        setIsContextMenuTreeNodeDataset(node.nodeType === "dataset");
        setContextDatasetId(node.nodeType === "dataset" ? node.datasetId : "")
        setContextDatasetType(datasetType)
        setContextTreeId(_treeData?.id)
        setContextMenuOptions(getContextMenuItem(node, treeType))
        setTreeContextNode(node)
      },
      [
        openContextMenu,
        setContextOpenNode,
        setIsContextMenuTreeNodeDataset,
        setContextDatasetId,
        setContextDatasetType,
        setContextTreeId,
        setContextMenuOptions,
        setTreeContextNode,
        getContextMenuItem
      ]
  )

  const treeNodesOnContextMenu = (e: any, nodes: ITreeNode[], treeType: string, _treeData: any) => {
    // Shows available actions in a context menu when right clicking with multiple checked nodes
    openContextMenu(e)
    setContextMenuOptions(getContextMenuItem(treeContextNode, treeType))
  }

  const treeNodeOnClick = React.useCallback(
      (_e: any, nodes: ITreeNode, treeType: string, _treeData: any) => {
        if (treeType === MERGED_DATASET_TREE_TYPE && nodes.nodeType === "folder" && nodes?.meta?.type === "root") {
          setSelected(nodes?.nodeId)
          setSelectedDatasetId(nodes?.nodeId)
          setSelectedDatasetNodeId(nodes?.nodeId)
        } else if (nodes.nodeType === "dataset") {
          setSelectedDatasetId(nodes?.datasetId)
          setSelectedDatasetNodeId(nodes?.nodeId)
          if (treeType === SINGLE_DATASET_TREE_TYPE) {
            setSelected(nodes?.nodeId)
          }
        }
      },
      [
        setSelected,
        setSelectedDatasetId,
        setSelectedDatasetNodeId,
        MERGED_DATASET_TREE_TYPE,
        SINGLE_DATASET_TREE_TYPE
      ]
  )

  function handleRawCheckedNodes(checked: boolean, checkedNode: ITreeNode) {
    const childrenOfCheckedNode: ITreeNode[] = getChildById(props.treeObjectData.tree, checkedNode.nodeId)
    let array = checked
      ? [...rawCheckedNodes, ...childrenOfCheckedNode]
      : rawCheckedNodes.filter((item: ITreeNode) => !childrenOfCheckedNode.some((child: ITreeNode) => child.nodeId === item.nodeId))

    setRawCheckedNodes(array)
  }

  const getCanMergeDatasets = (checkedNodes: ITreeNode[]) => {
    const filteredNodesThatHaveDatasetChecked = checkedNodes.filter((node: ITreeNode) => node?.nodeType == 'dataset')

    if (filteredNodesThatHaveDatasetChecked.length < 2) return false

    for (let i = 0; i < filteredNodesThatHaveDatasetChecked.length; i++) {
      if (filteredNodesThatHaveDatasetChecked[i]?.executionStatus !== EDatasetExecutionStatus.COMPLETED)
        return false

    }
    return true
  }

  const helperSetCanDeleteDatasets = (checkedNodes: ITreeNode[]) => {
    let deleteStatusAllow = true

    const buildFolderMap = (node: ITreeNode, gmid: string, userIsAdmin: boolean,
                            folderMap: Map<string, { id: string, canDelete: boolean }[]>,
                            nestedFolders: Set<string>) => {
      if (node.nodeType === 'folder' || node.nodeType === 'study') {
        const children = node.children || [];
        const datasets: any[] = [];

        const processChild = (child: ITreeNode) => {
          if (child.nodeType === 'dataset') {
            datasets.push({
              id: child.nodeId,
              canDelete: canDeleteItem(child, gmid, userIsAdmin)
            });
          } else if (child.nodeType === 'folder' || child.nodeType === 'study') {
            nestedFolders.add(child.nodeId);
            (child.children || []).forEach(processChild);
          }
        };

        children.forEach(processChild);
        folderMap.set(node.nodeId, datasets);
      }
    };
    const validateFolders = (folderMap: Map<string, { id: string, canDelete: boolean }[]>) => {
      for (const [folderId, children] of folderMap.entries()) {
        // if empty folder skip
        if (children.length === 0) {
          continue;
        }
        if (!children.some(child => child.canDelete)) {
          return false;
        }
      }
      return true;
    };

    const validateDatasets = (checkedNodes: ITreeNode[], folderMap: Map<string, { id: string, canDelete: boolean }[]>) => {
      // for datasets not in folders that are being deleted, check if they can be deleted
      for (const node of checkedNodes) {
        if (node.nodeType === 'dataset') {
          let found = false
          for (const [folderId, children] of folderMap.entries()) {
            if (children.some(child => child.id === node.nodeId)) {
              found = true
              break;
            }
          }
          if (found) {
            continue;
          }
          if (!canDeleteItem(node, gmid, isUserAdmin)) {
            return false
          }
        }
      }
      return true
    };

    let canDelete = true;
    const folderMap = new Map<string, { id: string, canDelete: boolean }[]>();
    const nestedFolders = new Set<string>();

    checkedNodes.forEach(node => buildFolderMap(node, gmid, isUserAdmin, folderMap, nestedFolders))

    nestedFolders.forEach(folderId => folderMap.delete(folderId));

    const areFoldersValid = validateFolders(folderMap);
    const areDatasetsValid = validateDatasets(checkedNodes, folderMap);

    if (!areFoldersValid || !areDatasetsValid) {
      canDelete = false;
    }

    const _canDeleteDataset = canDelete && deleteStatusAllow && checkedNodes.length >= 1

    return _canDeleteDataset
  }
  const helperSetCanExecuteDatasets = (checkedNodes: ITreeNode[]) => {
    let executionStatusAllowsGenerate = true
    const filteredNodesThatHaveExecutionStatusAndAreAllowed = checkedNodes.filter((node: ITreeNode) => node?.executionStatus != null && node?.datasetId != null && !SET_SHOULD_NOT_ALLOW_GENERATE.has(node?.executionStatus))
    if (filteredNodesThatHaveExecutionStatusAndAreAllowed.length === 0) {
      executionStatusAllowsGenerate = false
    }
    const _canExecuteDataset = executionStatusAllowsGenerate && checkedNodes.length >= 1

    return _canExecuteDataset
  }
  const getNodesForExecution = (checkedNodes: ITreeNode[]) => {
    const filteredNodesThatHaveExecutionStatusAndAreAllowed = checkedNodes.filter((node: ITreeNode) => node?.executionStatus != null && node?.datasetId != null && !SET_SHOULD_NOT_ALLOW_GENERATE.has(node?.executionStatus))
    return filteredNodesThatHaveExecutionStatusAndAreAllowed
  }
  const getDatasetIdsForExecution = (checkedNodes: ITreeNode[]) => {
    const filteredNodesThatHaveExecutionStatusAndAreAllowed = getNodesForExecution(checkedNodes)
    return filteredNodesThatHaveExecutionStatusAndAreAllowed.map((node: ITreeNode) => node?.datasetId ?? '')
  }

  function handleMergedCheckedNodes(checked: boolean, checkedNode: ITreeNode) {
    let newCheckedNodes = checked ? [...mergedCheckedNodes, checkedNode] : mergedCheckedNodes.filter((item: ITreeNode) => item.nodeId !== checkedNode.nodeId)
    // TODO: block regenerate if none of the datasets are outdated. So need to track all the children, and then when use it later for regen, filter only root; or, track separately.
    setMergedCheckedNodes(newCheckedNodes)
  }

  const selectedDatasetIds = useMemo(() => {
    const filteredRawCheckedNodes = rawCheckedNodes.filter((node: ITreeNode) => node?.datasetId != null)
    return filteredRawCheckedNodes.map((node: ITreeNode) => node.datasetId)
  }, [rawCheckedNodes])

  /**
   * Execute datasets
   */
  let isInProgress = false;
  if (executionStatusMap) {
    const statuses: EDatasetExecutionStatus[] = Object.values(executionStatusMap);
    for (let i = 0; i < statuses.length; i++) {
      if (IN_PROGRESS_STATUSES.has(statuses[i])) {
        isInProgress = true;
        break;
      }
    }
  }
  const varCanExecuteDataset = useMemo(() => {
    return helperSetCanExecuteDatasets(rawCheckedNodes)
  }, [rawCheckedNodes])
  const isGenerateButtonDisabled = !varCanExecuteDataset || !hasWriteAccess

  const checkIfNodesInProgress=((nodes:any) => {
    for(let i = 0; i < nodes.length; i++){
      if(nodes[i]?.executionStatus === EDatasetExecutionStatus.PENDING || nodes[i]?.executionStatus === EDatasetExecutionStatus.IN_PROGRESS)
        return true
    }
    return false
  })
  const varCanDeleteDataset = useMemo(() => {
    const areNodesInProgress = checkIfNodesInProgress(rawCheckedNodes)
    return helperSetCanDeleteDatasets(rawCheckedNodes) && !areNodesInProgress
  }, [rawCheckedNodes, executionStatusMap])
  const isDeleteButtonDisabled = !varCanDeleteDataset || !hasWriteAccess
  /**
   * Merge datasets
   */

  const varcanMergeDatasets = useMemo(() => {
    const isMergeCurrentlyHappening = checkIfMergeIsHappening()
    return getCanMergeDatasets(rawCheckedNodes) && !isMergeCurrentlyHappening
  }, [rawCheckedNodes,selectedMergeSetExecutionStatus])
  const isMergeButtonDisabled = !varcanMergeDatasets || !hasWriteAccess

  /**
   * Bulk Edit datasets
   */
  const canBulkEditDatasets = useMemo(() => {
    const filteredNodesThatHaveDatasetChecked =
      rawCheckedNodes.filter((node: ITreeNode) => node?.nodeType == 'dataset' &&
        node?.executionStatus != EDatasetExecutionStatus.PENDING &&
        node?.executionStatus != EDatasetExecutionStatus.IN_PROGRESS
      )
    return filteredNodesThatHaveDatasetChecked.length > 1
  }, [rawCheckedNodes])
  const isBulkEditButtonDisabled = !hasWriteAccess || !canBulkEditDatasets || isReadOnlyStudy
  const [isBulkEditDialogOpen, setBulkEditDialogOpen] = useState<boolean>(false)

  /**
   * Cancel multiple analyses
   */
  const isCancelMultipleAnalysesDisabled = useMemo(() => {
    let disabled = true
    for (let i = 0; i < rawCheckedNodes.length; i++) {
      if (IN_PROGRESS_STATUSES.has(rawCheckedNodes[i]?.executionStatus)) { // TODD: Don't allow cancel 'pending' until implement on backend
        // if (rawCheckedNodes[i]?.executionStatus === EDatasetExecutionStatus.IN_PROGRESS) {
        return false
      }
    }
    return disabled || !hasWriteAccess
  }, [rawCheckedNodes])

  const [generateDatasetsDialogData, setGenerateDatasetsDialogData] = useState<any>(null)
  const onClickGenerate = () => {
    // if all the execution statuses are not in progress, then don't show the dialog
    let showDialog = false
    for (let i = 0; i < rawCheckedNodes.length; i++) {
      const status = rawCheckedNodes[i]?.executionStatus
      if (status != null && SET_SHOULD_NOT_ALLOW_GENERATE.has(status)) {
        showDialog = true
      }
    }
    if (showDialog) {
      setGenerateDatasetsDialogData(true)
    } else {
      generateCheckedDatasets()
    }
  }
  const [cancelDatasetsDialogData, setCancelDatasetsDialogData] = useState<any>(null)
  const onClickCancelCheckedDatasets = () => {
    setCancelDatasetsDialogData(true)
  }

  const onClickDelete = (dataSet: any, flag: boolean) => {
    if (isDeletingMerge)
      return;
    if (isDeleting)
      return;
    setMergeDeleteFlag(flag)
    setIsDeleteAppDialogOpen(true);
    setDataSetToDelete(dataSet)
    setRedlineAppDialogType(REDLINE_DIALOG_OPTIONS.DELETE);
    setRedlineAppDialogTitle("Delete folders/datasets");
  }

  const deleteIconEnable = (flag: boolean) => {
    return flag && {
      color: '#8800ff',
      animation: "shake .4s infinite",
      "@keyframes shake": {
        '0%, 100%': {transform: 'rotate(0deg)'},
        '25%': {transform: 'rotate(5deg)'},
        '75%': {transform: 'rotate(-5deg)'}
      }
    }
  }
  const [lastSelectedItem, setLastSelectedItem] = React.useState(null);

  const handleAddMultipleNodesToClipboard = () => {
    dispatch(setCoverageRequirementsClipboard({"nodeToCopy": rawCheckedNodes?.map((rawNode: any) => rawNode?.nodeId), "isSingleCopy": false}))
    toast.success("Added Checked Items to Clipboard")
  }

  const packagesDataFetchHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchAdasPrograms)
  const [adasProgramsData, setAdasProgramsData] = useState([])
  const [adasProgramsToModelYears, setAdasProgramsToModelYears] = useState<any>({})

  const fetchPackageData = () => {
    packagesDataFetchHandle().then((data) => {
      const _adasProgramsToModelYears: any = {}
      data.forEach((element: any) => {
        const elementData = element?.data
        const programName = elementData?.programName
        const modelYear = elementData?.modelYear
        if (programName in _adasProgramsToModelYears) {
          _adasProgramsToModelYears[programName].push(modelYear)
        } else {
          _adasProgramsToModelYears[programName] = [modelYear]
        }
      })
      setAdasProgramsData(data)
      setAdasProgramsToModelYears(_adasProgramsToModelYears)
    }).catch((e) => {
      console.error(e)
      toast.error("Failed to fetch Adas Programs data")
    })
  }

  const [updateOwnerDialogOpen, setUpdateOwnerDialogOpen] = useState<boolean>(false)
  const onClickUpdateOwnerOfSelectedDatasets = () => {
    setUpdateOwnerDialogOpen(true)
  }

  if (!props.treeObjectData || !props.treeType) return null
  return (
    <ArgosFlexbox vertical noGap style={{overflow: 'auto', height: '100%'}}>
      {/*Toolbar*/}
      <ArgosFlexbox noGap style={{
        justifyContent: "left",
        alignContent: "center",
        gap: "0px",
        borderTop: "2px solid",
        borderColor: theme.layout.header.backgroundColor,
      }}>
        {props?.treeType == SINGLE_DATASET_TREE_TYPE ?
          <>
            <Tooltip title="Generate" arrow placement='top'>
              <LoadingButton
                variant="text"
                // loading={shouldShowLoading}
                loadingPosition="start"
                startIcon={<ArgosExecuteIcon/>}
                sx={{textTransform: 'none'}}
                onClick={onClickGenerate}
                // disabled={!canExecuteDataset || !hasWriteAccess || isInProgress}
                disabled={isDeleting || isGenerateButtonDisabled || isReadOnlyStudy}
              >
                {/*Generate*/}
              </LoadingButton>
            </Tooltip>

            <Tooltip title="Cancel" arrow placement='top'>
              <LoadingButton
                variant="text"
                //loading={shouldShowCancellingLoader}
                loadingPosition="start"
                startIcon={<ArgosCancelIcon/>}
                sx={{textTransform: 'none'}}
                onClick={() => onClickCancelCheckedDatasets()}
                disabled={isCancelMultipleAnalysesDisabled || isReadOnlyStudy}
              >
                {/*shouldShowCancellingLoader ? "Cancelling" : "Cancel"*/}
              </LoadingButton>
            </Tooltip>

            {/*right border*/}
            <div style={{borderRight: "2px solid", borderColor: theme.layout.header.backgroundColor}}>
            </div>

            <Tooltip title={isDeleting ? "Deleting" : "Delete"} arrow placement='top'>
              <LoadingButton
                variant="text"
                // loading={shouldShowLoading}
                loadingPosition="start"
                startIcon={rawCheckedNodes.length > 1 ? <ArgosDeleteAllIcon sx={deleteIconEnable(isDeleting)}/> : <ArgosDeleteIcon sx={deleteIconEnable(isDeleting)}/>}
                sx={{textTransform: 'none'}}
                disabled={isMerging || isDeleteButtonDisabled || isReadOnlyStudy}
                onClick={() => onClickDelete(rawCheckedNodes, false)}
              >
                {/*Delete*/}
              </LoadingButton>
            </Tooltip>

            <Tooltip title={"Copy All Checked Items"} arrow placement='top'>
              <LoadingButton
                variant="text"
                loadingPosition="start"
                startIcon={rawCheckedNodes.length > 1 ? <ArgosCopyAll/> : <ArgosCopy/>}
                sx={{textTransform: 'none'}}
                disabled={isMerging || isDeleteButtonDisabled || isReadOnlyStudy}
                onClick={() => handleAddMultipleNodesToClipboard()}
              >
              </LoadingButton>
            </Tooltip>

            <Tooltip title="Bulk Edit">
              <LoadingButton
                variant="text"
                startIcon={<ArgosBulkEditIcon/>}
                sx={{textTransform: 'none'}}
                disabled={isBulkEditButtonDisabled}
                onClick={() => {
                  setBulkEditDialogOpen(true)
                }}
              >
                {/*Bulk Edit All Properties*/}
              </LoadingButton>
            </Tooltip>

            <Tooltip title={isMerging ? "Merging" : "Merge"} arrow placement='top'>
              <LoadingButton
                variant="text"
                startIcon={<ArgosMergeIcon sx={isMerging && {
                  color: '#8800ff', animation: "merge 1s infinite",
                  "@keyframes merge": {
                    "0%": {
                      transform: "scale(1)",
                    },
                    "50%": {
                      transform: "scale(1.2)",
                    },
                    "100%": {
                      transform: "scale(1)",
                    },
                  }
                }}/>}
                sx={{textTransform: 'none', fontSize: '14px'}}
                onClick={onClickMerge}
                disabled={isMergeButtonDisabled || isReadOnlyStudy}
              >
                {/*Merge*/}
              </LoadingButton>
            </Tooltip>
            <Tooltip title="Update owner of selected datasets" arrow placement="top">
              <IconButton
                onClick={onClickUpdateOwnerOfSelectedDatasets}
                disabled={rawCheckedNodes.length === 0 || isReadOnlyStudy}
                color={"primary"}
              >
                <BiUser/>
              </IconButton>
            </Tooltip>

            <div>
              <Tooltip title={'Display owner of datasets'} arrow placement="top">
                <div>
                  <ArgosCheckbox checked={isCheckedDisplayOwner} onChange={onCheckDisplayOwner}/>
                </div>
              </Tooltip>
            </div>
          </>
          :
          <>
            <Tooltip title={isRegenerating ? "Regenerating" : "Regenerate"} arrow placement='top'>
              <LoadingButton
                variant="text"
                //loading={isRegenerating}
                loadingPosition="start"
                startIcon={<ArgosReloadIcon sx={isRegenerating && {
                  color: '#8800ff',
                  animation: "spin 2s linear infinite",
                  "@keyframes spin": {
                    "0%": {
                      transform: "rotate(0deg)",
                    },
                    "100%": {
                      transform: "rotate(360deg)",
                    },
                  },
                }}/>}
                sx={{textTransform: 'none'}}
                onClick={() => regenerateOutdated(true)}
                disabled={!canRegenerate || !hasWriteAccess || isDeletingMerge || isReadOnlyStudy}
              >
                {/*Regenerate Outdated*/}
              </LoadingButton>
            </Tooltip>
            <Tooltip title={isDeletingMerge ? "Deleting" : "Delete"} arrow placement='top'>
              <LoadingButton
                variant="text"
                // loading={shouldShowLoading}
                loadingPosition="start"
                startIcon={mergedCheckedNodes.length > 1 ? <ArgosDeleteAllIcon sx={deleteIconEnable(isDeletingMerge)}/> : <ArgosDeleteIcon sx={deleteIconEnable(isDeletingMerge)}/>}
                sx={{textTransform: 'none'}}
                disabled={!canRegenerate || !hasWriteAccess || isReadOnlyStudy}
                onClick={() => onClickDelete(mergedCheckedNodes, true)}
              >
                {/*Delete*/}
              </LoadingButton>
            </Tooltip>
            <div>
              <Tooltip title={'Display owner of datasets and merge sets'} arrow placement="top">
                <div>
                  <ArgosCheckbox checked={isCheckedDisplayOwner} onChange={onCheckDisplayOwner}/>
                </div>
              </Tooltip>
            </div>
          </>
        }
      </ArgosFlexbox>

      {/*Tree*/}
      <ArgosFlexbox noGap style={{overflow: 'auto', flex: 1, marginTop: 5}}>
        <CheckboxDraggableTree
          treeType={props.treeType}
          treeData={props.treeObjectData}
          treeNodeOnClick={treeNodeOnClick}
          treeNodeOnContextMenu={treeNodeOnContextMenu}
          onChangeCheckedNodes={props.treeType === "Single" ? setRawCheckedNodes : setMergedCheckedNodes}
          renderTreeItemLabel={renderTreeItemLabel}
          isMergeDialogUp={isMerging}
          checkIfMerging={checkIfMergeIsHappening}
          isDeleting={isDeleting || isDeletingMerge}
          disableDragAndDrop={isReadOnlyStudy}
        />
      </ArgosFlexbox>

      {/*Context menu*/}
      <ArgosFlexbox noGap style={{overflow: 'auto'}}>
        {hasWriteAccess && <TreeItemContextMenu
          contextMenuOptions={contextMenuOptions}
          isTreeNodeContextMenuOpen={isTreeNodeContextMenuOpen}
          setIsTreeNodeContextMenuOpen={setIsTreeNodeContextMenuOpen}
          contextMenuTopLeftCoordinates={contextMenuTopLeftCoordinates}
          isContextMenuTreeNodeDataset={isContextMenuTreeNodeDataset}

          // RedlineAppDialog
          handleOpenRedlineAppDialog={handleOpenRedlineAppDialog}
          isRedlineAppDialogOpen={isRedlineAppDialogOpen}
          redlineAppDialogTitle={redlineAppDialogTitle}
          redlineAppDialogType={redlineAppDialogType}
          handleCloseRedlineAppDialog={handleCloseRedlineAppDialog}
          treeOperation={treeOperation}
          treeData={props?.treeObjectData?.tree}
          contextNodeId={contextNodeId}
          contextTreeId={contextTreeId}

          treeType={props.treeType}
        />}
      </ArgosFlexbox>
      {isDeleteAppDialogOpen &&
        <RedlineAppDialog
          action={() => deleteNodes(dataSetToDelete, props.treeType)}
          dialogtitle={redlineAppDialogTitle}
          dialogtype={redlineAppDialogType}
          open={isDeleteAppDialogOpen}
          onClose={handleCloseDeleteAppDialog}
          treeType={props.treeType}
          data={{nodes: dataSetToDelete}}
          treeData={props?.treeObjectData?.tree}
        />}
      {/*Regenerate merged data sets warning dialog*/}
      <ArgosDialog
        width={'auto'} height={'auto'} maxWidth={'300px'}
        open={mergedSetsToBeDeleted != null}
        onClose={() => {
          setMergedSetsToBeDeleted(null)
        }}
      >
        <ArgosFlexbox vertical noGap style={{overflow: 'auto', height: '100%'}}>
          <div style={{flex: 1, overflow: 'auto'}}>
            {`Are you sure you want to regenerate? The following merged sets will be deleted because they have less than 2 raw ${VAR_COMMON_CONSTANTS.VAR_DATA_SETS} to merge:
            ${mergedSetsToBeDeleted?.map((set: any) => set).join(', ')}`}
          </div>
          <BoxWithCenteredContent horizontal style={{marginTop: 15}}>
            <ArgosButton
              onClick={() => {
                setMergedSetsToBeDeleted(null)
              }}
            >
              CANCEL
            </ArgosButton>
            <ArgosButton
              onClick={() => {
                setMergedSetsToBeDeleted(null)
                regenerateOutdated(false)
              }}

            >
              OK
            </ArgosButton>
          </BoxWithCenteredContent>
        </ArgosFlexbox>
      </ArgosDialog>

      {/*Regenerate merged data sets summary dialog*/}
      <ArgosDialog
        width={'auto'} height={'40vh'} maxHeight={'40vh'}
        open={regenerateSummary != null}
        onClose={() => {
          setRegenerateSummary(null)
        }}
        headerTitle={`The following modifications were made to the merged data sets`}
        headerTitleAlign={'left'}
      >
        <ArgosFlexbox vertical noGap style={{overflow: 'auto', height: '100%'}}>
          <div style={{flex: 1, overflow: 'auto'}}>
            <NestedBulletList data={regenerateSummary}/>
          </div>
          <BoxWithCenteredContent horizontal style={{marginTop: 15}}>
            <ArgosButton
              onClick={() => {
                setRegenerateSummary(null)
              }}
            >
              OK
            </ArgosButton>
          </BoxWithCenteredContent>
        </ArgosFlexbox>
      </ArgosDialog>


      {/*Generate Datasets Dialog*/}
      <ArgosDialog
        width={'auto'} height={'auto'} maxWidth={'300px'}
        open={generateDatasetsDialogData != null}
        onClose={() => {
          setGenerateDatasetsDialogData(null)
        }}
      >
        <ArgosFlexbox vertical noGap style={{overflow: 'auto', height: '100%'}}>
          <div style={{flex: 1, overflow: 'auto'}}>
            Generate all selected data sets that are not in progress?
          </div>
          <BoxWithCenteredContent horizontal style={{marginTop: 15}}>
            <ArgosButton
              onClick={() => {
                setGenerateDatasetsDialogData(null)
              }}
            >
              CANCEL
            </ArgosButton>
            <ArgosButton
              onClick={() => {
                setGenerateDatasetsDialogData(null)
                generateCheckedDatasets()
              }}
            >
              OK
            </ArgosButton>
          </BoxWithCenteredContent>
        </ArgosFlexbox>
      </ArgosDialog>

      {/* Copy and Paste Dataset Dialog- Original Within Same Study */}
      <ArgosDialog
        maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
        open={isCopyPasteDialogOpen}
        onClose={() => {
          setIsCopyPasteDialogOpen(false)
        }}
        headerTitle={'Paste Data Set'}
        headerTitleAlign={'left'}
      >
        <BoxWithCenteredContent horizontal vertical
                                style={{height: '100%', display: 'flex', flexFlow: 'column nowrap'}}>
          <p style={{width: '300px', textAlign: 'center'}}>This action will paste the data into the selected folder</p>
          <ArgosFlexbox>
            <ArgosCheckbox
              checked={includeAnalysis}
              onChange={handleCheckboxChange}
            />
            <Typography>Include Analysis Results in Copy</Typography>
          </ArgosFlexbox>
          <ArgosFlexbox>
            <ArgosButton
              onClick={() => {
                handleCopyPaste(modifyData, contextNodeId, "folder", TREE_OPERATIONS.PASTE);
                setIsCopyPasteDialogOpen(false);
              }}
            >
              CONFIRM
            </ArgosButton>
            <ArgosButton
              onClick={() => setIsCopyPasteDialogOpen(false)}>CANCEL</ArgosButton>
          </ArgosFlexbox>
        </BoxWithCenteredContent>
      </ArgosDialog>


      {/* Start - Copy Paste to Another Study Dialog */}
      <ArgosDialog
        maxWidth={'fit-content'} minWidth={'0px'} height={'fit-content'}
        // open and onclose need to be changed for this popup
        open={isCopyPasteDialogOpenStudies}
        onClose={() => {
          setIsCopyPasteDialogOpenStudies(false)
        }}
        headerTitle={'Copy Raw Data Set to Study -- Choose Destination Folder'}
        headerTitleAlign={'left'}
      >
        <ArgosFlexbox style={{display: 'flex', flexDirection: 'row', height: '100%', width: '100%'}}>
          <ArgosFlexbox noGap style={{overflow: 'auto', flex: '1 1 0%', height: '100%'}}>
            <Parent/>
          </ArgosFlexbox>

          {/* Rest of Dialog Contents */}
          <BoxWithCenteredContent horizontal vertical
                                  style={{height: '100%', width: WIDTH, display: 'flex', flexFlow: 'column nowrap', flex: '3 1 0%'}}>
            <p style={{width: '300px', textAlign: 'center'}}>This action will paste the data into the selected folder</p>
            <ArgosFlexbox style={{justifyContent: "center", alignItems: "center"}}>


              {/* ArgosSelect Drop Downs go here */}
              <FormControl sx={{width: WIDTH / 3}} disabled={shouldBulkEditPackagePropertyForCopyDatasetToOtherStudy !== EBulkEditPackageProperty.BULK_EDIT}>
                <InputLabel id="category-select-label">
                  Adas Program
                </InputLabel>
                <Select
                  labelId="category-select-label"
                  id="category-select"
                  value={selectedAdasProgramName}
                  label="Adas Program"
                  onChange={e => setSelectedAdasProgramName(e.target.value)}
                  variant="standard"
                >
                  {Object.keys(adasProgramsToModelYears).map((option: string) => (
                    <ArgosMenuItem key={option} value={option}>
                      <ListItemText
                        primary={option}
                      />
                    </ArgosMenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{width: WIDTH / 3}} disabled={shouldBulkEditPackagePropertyForCopyDatasetToOtherStudy !== EBulkEditPackageProperty.BULK_EDIT}>
                <InputLabel id="property-select-label">
                  Model Year
                </InputLabel>
                <Select
                  labelId="property-select-label"
                  id="property-select"
                  value={selectedModelYear}
                  label="Model Year"
                  onChange={e => setSelectedModelYear(e.target.value)}
                  variant="standard"
                >
                  {adasProgramsToModelYears[selectedAdasProgramName]?.map((option: string) => (
                    <ArgosMenuItem key={option} value={option}>
                      <ListItemText primary={option}/>
                    </ArgosMenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* End of Argos Select Drop Down */}

            </ArgosFlexbox>
            <ArgosFlexbox vertical style={{alignItems: "center"}}>
              <ArgosFlexbox>
                <RadioGroup value={shouldBulkEditPackagePropertyForCopyDatasetToOtherStudy} onChange={handleRadioChange}>
                  <FormControlLabel
                    value={EBulkEditPackageProperty.DO_NOT_CHANGE}
                    control={<Radio/>}
                    label="Do Not Change Package Property"
                  />
                </RadioGroup>

                <RadioGroup value={shouldBulkEditPackagePropertyForCopyDatasetToOtherStudy} onChange={handleRadioChange}>
                  <FormControlLabel
                    value={EBulkEditPackageProperty.BULK_EDIT}
                    control={<Radio/>}
                    label="Bulk Edit Package Property"
                  />
                </RadioGroup>


              </ArgosFlexbox>

              <ArgosFlexbox style={{alignItems: "center", flexDirection: 'row'}}>
                <ArgosCheckbox
                  checked={includeAnalysis}
                  onChange={handleCheckboxChange}
                />
                <Typography>Include Analysis Results in Copy</Typography>
              </ArgosFlexbox>
            </ArgosFlexbox>

            <ArgosFlexbox>
              <ArgosButton
                onClick={() => {
                  copyNodeToStudy()
                }}
              >
                CONFIRM
              </ArgosButton>
              <ArgosButton
                onClick={() => setIsCopyPasteDialogOpenStudies(false)}>CANCEL</ArgosButton>
            </ArgosFlexbox>
          </BoxWithCenteredContent>
        </ArgosFlexbox>
      </ArgosDialog>
      {/* End - Copy Paste to Another Study Dialog */}


      {/*Cancel Datasets Dialog*/}
      <ArgosDialog
        width={'auto'} height={'auto'} maxWidth={'300px'}
        open={cancelDatasetsDialogData != null}
        onClose={() => setCancelDatasetsDialogData(null)}
      >
        <ArgosFlexbox vertical noGap style={{overflow: 'auto', height: '100%'}}>
          <div style={{flex: 1, overflow: 'auto'}}>
            Cancel all selected runs that are in progress?
          </div>
          <BoxWithCenteredContent horizontal style={{marginTop: 15}}>
            <ArgosButton
              onClick={() => {
                setCancelDatasetsDialogData(null)
              }}
            >
              CANCEL
            </ArgosButton>
            <ArgosButton
              onClick={() => {
                setCancelDatasetsDialogData(null)
                handleCancelMultipleAnalysis()
              }}
            >
              OK
            </ArgosButton>
          </BoxWithCenteredContent>
        </ArgosFlexbox>
      </ArgosDialog>

      {/*Merge Datasets Dialog*/}
      <StyledArgosDialog
        width={dialogWidth}
        height={dialogHeight}
        open={isMergeDatasetsDialogOpen}
        onClose={() => {
          setIsMergeDatasetsDialogOpen(false)
        }}
      >
        <MergeDatasetsDialog
          studyId={props?.studyId}
          studyName={props?.studyName}
          isPlanOfRecord={props?.isPlanOfRecord}
          selectedDatasets={selectedDatasetIds}
          userData={userData}
          handleTreeDataChange={setMergedDatasetsTreeData}
          handleTabIndexChange={setLeftSidebarTabIndex}
          setIsMerging={setIsMerging}
          onClickCancel={() => setIsMergeDatasetsDialogOpen(false)}
          onCloseDialog={() => setIsMergeDatasetsDialogOpen(false)}
        />
      </StyledArgosDialog>

      {/*Bulk Edit Selected Datasets Dialog*/}
      {isBulkEditDialogOpen &&
        <StyledArgosDialog
          width={800}
          height={"max-content"}
          open={isBulkEditDialogOpen}
          onClose={() => {
            setBulkEditDialogOpen(false)
          }}
        >
          <BulkEditDialog
            onCloseDialog={() => setBulkEditDialogOpen(false)}
            rawCheckedNodes={rawCheckedNodes}
            studyId={props.studyId}
            fetchStudy={props.fetchStudy}
          />
        </StyledArgosDialog>
      }
      {/*Update Owner Dialog*/}
      <ArgosDialog
        width={'auto'} maxHeight={'50vh'} maxWidth={'80vw'} minWidth={'400px'}
        open={updateOwnerDialogOpen}
        headerTitle={'Update owner of selected datasets'}
        headerTitleAlign={'left'}
      >
        <UpdateOwnerDialog
          nodes={rawCheckedNodes}
          onClose={() => setUpdateOwnerDialogOpen(false)}
        />
      </ArgosDialog>

    </ArgosFlexbox>
  )
}

export default CoverageRequirementsTreeComponent;

export const findNodeId = (root: any, favId: string): any => {
  if (favId == null) return null
  const id = root?.datasetId
  if (id === favId) {
    return root?.nodeId
  }
  for (let i = 0; i < root?.children?.length; ++i) {
    const child = root.children[i]
    const res: any = findNodeId(child, favId)
    if (res != null) {
      return res
    }
  }
  return null
}

const deleteFromStatusMapIfAbortedInTree = (tree: ITreeNode, _statusMap: any) => {
  if (_statusMap == null)
    return null

  if (tree?.nodeType == "dataset") {
    if (tree.datasetId != undefined && _statusMap[tree?.datasetId] != undefined && tree?.executionStatus == EDatasetExecutionStatus.ABORTED) {
      delete _statusMap[tree.datasetId]
      if (Object.keys(_statusMap).length == 0) {
        return null
      }
      return _statusMap
    }
  } else {
    tree?.children?.forEach((child: ITreeNode) => {
      deleteFromStatusMapIfAbortedInTree(child, _statusMap)
    })
  }
  return _statusMap
}

const getStatusMapOfTreeNodes = (tree: ITreeNode, _statusMap: any) => {
  if (_statusMap == null)
    return null

  if (tree?.nodeType == "dataset") {
    if (tree.datasetId != null && tree?.executionStatus != null) {
      _statusMap[tree.datasetId] = tree.executionStatus
      return _statusMap
    }
  } else {
    tree?.children?.forEach((child: ITreeNode) => {
      getStatusMapOfTreeNodes(child, _statusMap)
    })
  }
  return _statusMap
}

/**
 *
 * @param tree Selected tree node
 * @param gmid User's GMID
 * @param userIsAdmin
 * @returns True if User can delete the Dataset or Folder based on permissions.
 */
export const canDeleteItem = (
  tree: ITreeNode,
  gmid: string,
  userIsAdmin: boolean,
) => {
  // Helper function to check if the user owns any dataset in the folder or its nested folders
  const ownsDataset = (node: ITreeNode): boolean => {
    if (node.nodeType === 'dataset' && node.owner === gmid) {
      return true;
    }
    if (node.children) {
      for (const child of node.children) {
        if (ownsDataset(child)) {
          return true;
        }
      }
    }
    return false;
  };

  // Check if the user is an admin
  if (userIsAdmin) {
    return true;
  }

  // Check if the node is a folder and it is empty
  if (tree.nodeType === 'folder' && (!tree.children || tree.children.length === 0)) {
    return true;
  }

  // Check if the node is a folder and if the user owns at least one dataset inside it
  if (tree.nodeType === 'folder' && ownsDataset(tree)) {
    return true;
  }

  // Check if the node is a dataset and if the user owns it
  if (tree.nodeType === 'dataset' && tree.owner === gmid) {
    return true;
  }

  return false;
};

export const getNodeById = (nodes: ITreeNode, id: string) => {
  if (nodes?.nodeId === id) {
    return nodes
  } else if (Array.isArray(nodes?.children)) {
    let result = null;
    nodes.children.forEach(node => {
      if (!!getNodeById(node, id)) {
        result = getNodeById(node, id)
      }
    });
    return result
  }
  return null
}
